import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Popover, Typography } from "@mui/material";
import { styled } from "@mui/system";
import React, { useRef, useState } from "react";
import CountryFlag from "src/utils/CountryFlag";
import { SupportedLanguage } from "./disclosure.types";
import { LANGUAGE_OPTIONS, getLanguageOption } from "./disclosures.config";

// Map language codes to country codes for flags
const LANGUAGE_TO_COUNTRY: Record<SupportedLanguage, string> = {
  en: "us",
  es: "es",
  zh: "cn",
  vi: "vn",
  so: "so",
  hmn: "la",
};

interface LanguageSelectorProps {
  selectedLanguage: SupportedLanguage;
  onLanguageChange: (language: SupportedLanguage) => void;
  supportedLanguages?: SupportedLanguage[];
}

const SelectorContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  padding: "8px 12px",
  borderRadius: "8px",
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: "#F3F4F6",
  },
});

const DropdownContainer = styled(Box)({
  display: "flex",
  width: "116px",
  padding: "12px 0",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  borderRadius: "0 0 8px 8px",
  background: "#FFF",
  boxShadow: "0 12px 24px 0 rgba(216, 216, 218, 0.80)",
});

const DropdownHeader = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
  color: "#002035",
  padding: "0 12px",
  width: "100%",
});

const LanguageItem = styled(Box)<{ selected: boolean }>(({ selected }) => ({
  display: "flex",
  padding: "8px 12px",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  cursor: "pointer",
  backgroundColor: selected ? "#EFF6FF" : "transparent",
  "&:hover": {
    backgroundColor: selected ? "#EFF6FF" : "#F9FAFB",
  },
}));

const LanguageLabel = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
  color: "#002035",
});

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  selectedLanguage,
  onLanguageChange,
  supportedLanguages,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (language: SupportedLanguage) => {
    onLanguageChange(language);
    handleClose();
  };

  const currentLanguage = getLanguageOption(selectedLanguage);
  const availableLanguages = supportedLanguages
    ? LANGUAGE_OPTIONS.filter((l) => supportedLanguages.includes(l.code))
    : LANGUAGE_OPTIONS;

  return (
    <>
      <SelectorContainer ref={containerRef} onClick={handleClick}>
        {currentLanguage && (
          <CountryFlag countrycode={LANGUAGE_TO_COUNTRY[currentLanguage.code]} size={20} />
        )}
        {open ? (
          <KeyboardArrowUp sx={{ color: "#6B7280", fontSize: "20px" }} />
        ) : (
          <KeyboardArrowDown sx={{ color: "#6B7280", fontSize: "20px" }} />
        )}
      </SelectorContainer>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "0 0 8px 8px",
              boxShadow: "none",
              marginTop: "4px",
              overflow: "hidden",
              padding: 0,
            },
          },
        }}>
        <DropdownContainer>
          <DropdownHeader>Select language</DropdownHeader>
          {availableLanguages.map((lang) => (
            <LanguageItem
              key={lang.code}
              selected={lang.code === selectedLanguage}
              onClick={() => handleLanguageSelect(lang.code)}>
              <CountryFlag countrycode={LANGUAGE_TO_COUNTRY[lang.code]} size={16} />
              <LanguageLabel>{lang.label}</LanguageLabel>
            </LanguageItem>
          ))}
        </DropdownContainer>
      </Popover>
    </>
  );
};

export default LanguageSelector;
